import React from 'react'
import type { PresentationBlock } from 'paintscout'

import SwapHorizIcon from '@material-ui/icons/SwapHoriz'
import VerticalAlignTopIcon from '@material-ui/icons/VerticalAlignTop'
import VerticalAlignCenterIcon from '@material-ui/icons/VerticalAlignCenter'
import VerticalAlignBottomIcon from '@material-ui/icons/VerticalAlignBottom'
import NotInterestedIcon from '@material-ui/icons/NotInterested'
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt'
import ZoomOutMapIcon from '@material-ui/icons/ZoomOutMap'
import GradientIcon from '@material-ui/icons/Gradient'
import CodeIcon from '@material-ui/icons/Code'
import AspectRatioIcon from '@material-ui/icons/AspectRatio'
import { ButtonSelectOption, ButtonOption, SliderOption } from '../../components'
import { UTurnRightIcon, DescriptionIcon, ImageSmallIcon } from '@paintscout/ui'

interface TwoColumnOptionsProps {
  block: PresentationBlock
  handleOptionFieldChange: (field: string, value: any) => void
}

function TwoColumnOptions(props: TwoColumnOptionsProps) {
  const { block, handleOptionFieldChange } = props
  const fields = block?.fields ?? ({} as any)
  const { animation, rightType, leftType, columnAlign, leftWidth = 6, fullWidthImage } = fields

  const handleSwapClick = async () => {
    handleOptionFieldChange('fields.swap', true)
  }

  const getIcon = (type: string) => {
    if (type === 'text') {
      return <DescriptionIcon />
    } else if (type === 'image') {
      return <ImageSmallIcon />
    } else if (type === 'custom') {
      return <CodeIcon />
    }
  }

  const handleWidthChange = (leftWidth: number) => {
    handleOptionFieldChange('fields.leftWidth', leftWidth)
    handleOptionFieldChange('fields.rightWidth', 12 - leftWidth)
  }

  const options = [
    {
      Component: (
        <ButtonSelectOption
          label={'Animation'}
          value={animation}
          buttons={[
            { icon: <NotInterestedIcon />, value: 'none' },
            { icon: <ArrowRightAltIcon style={{ transform: 'rotate(90deg)' }} />, value: 'slide' },
            { icon: <UTurnRightIcon style={{ transform: 'rotate(180deg)' }} />, value: 'spring' },
            { icon: <GradientIcon />, value: 'fade' },
            { icon: <ZoomOutMapIcon />, value: 'zoom' }
          ]}
          onChange={(value) => handleOptionFieldChange('fields.animation', value)}
        />
      ),
      tab: 'content'
    },
    {
      Component: (
        <ButtonSelectOption
          label={'Full Width Image'}
          value={fullWidthImage ?? false}
          buttons={[{ icon: <AspectRatioIcon />, value: true }]}
          onChange={(_value) => handleOptionFieldChange('fields.fullWidthImage', !fields.fullWidthImage)}
        />
      ),
      tab: 'content'
    },
    {
      Component: (
        <SliderOption
          label={'Column Sizes'}
          value={leftWidth}
          min={1}
          max={11}
          marks={[{ value: 3 }, { value: 4 }, { value: 5 }, { value: 6 }, { value: 7 }, { value: 8 }, { value: 9 }]}
          onChange={(value) => handleWidthChange(value)}
        />
      ),
      tab: 'content'
    },
    {
      Component: (
        <ButtonSelectOption
          label={'Column Alignment'}
          value={columnAlign}
          buttons={[
            { icon: <VerticalAlignTopIcon />, value: 'top' },
            { icon: <VerticalAlignCenterIcon />, value: 'center' },
            { icon: <VerticalAlignBottomIcon />, value: 'bottom' }
          ]}
          onChange={(value) => handleOptionFieldChange('fields.columnAlign', value)}
        />
      ),
      tab: 'content'
    },
    {
      Component: (
        <ButtonOption
          label={'Swap Columns'}
          buttons={[
            { icon: getIcon(leftType) },
            { icon: <SwapHorizIcon />, onClick: () => handleSwapClick() },
            { icon: getIcon(rightType) }
          ]}
        />
      ),
      tab: 'content'
    },
    {
      Component: (
        <ButtonSelectOption
          label={'Left Content Type'}
          value={leftType}
          buttons={[
            { icon: <ImageSmallIcon />, value: 'image' },
            { icon: <DescriptionIcon />, value: 'text' }
          ]}
          onChange={(value) => handleOptionFieldChange('fields.leftType', value)}
        />
      ),
      tab: 'content'
    },
    {
      Component: (
        <ButtonSelectOption
          label={'Right Content Type'}
          value={rightType}
          buttons={[
            { icon: <ImageSmallIcon />, value: 'image' },
            { icon: <DescriptionIcon />, value: 'text' }
          ]}
          onChange={(value) => handleOptionFieldChange('fields.rightType', value)}
        />
      ),
      tab: 'content'
    }
  ]

  return options
}

export default TwoColumnOptions
